import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageHero } from "./About";
import { apiClient } from "../lib/api";
import { PillLabel } from "../components/UI";
import { ArrowLeft, Calendar } from "lucide-react";

export default function News() {
  const { t } = useTranslation();
  const [articles, setArticles] = useState([]);
  useEffect(() => { apiClient.get("/articles?limit=50").then(r => setArticles(r.data)).catch(()=>setArticles([])); }, []);
  return (
    <div data-testid="page-news">
      <PageHero eyebrow="Newsroom" title="Industry insights and alliance updates" subtitle="Market reports, alliance news and events from across Europe." image="https://images.pexels.com/photos/8463167/pexels-photo-8463167.jpeg"/>
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {articles.length === 0 ? (
            <div className="text-center text-[#4A5568] py-20">No articles yet.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((a, i) => (
                <Link key={a.id} to={`/news/${a.slug}`} data-testid={`news-item-${i}`} className="premium-card overflow-hidden block group">
                  {a.cover_image && (
                    <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                      <img src={a.cover_image} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
                    </div>
                  )}
                  <div className="p-6">
                    <PillLabel tone="muted">{a.category}</PillLabel>
                    <h3 className="mt-4 font-display font-semibold text-lg text-[#0B1120] leading-snug line-clamp-2 group-hover:text-[#003399] transition-colors">{a.title}</h3>
                    <p className="mt-2 text-sm text-[#4A5568] line-clamp-3">{a.excerpt}</p>
                    <div className="mt-4 text-xs text-slate-400 font-display flex items-center gap-2"><Calendar size={12}/> {new Date(a.created_at).toLocaleDateString()}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export function NewsDetail() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => {
    apiClient.get(`/articles/${slug}`).then(r => setArticle(r.data)).catch(()=>setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="pt-40 pb-24 max-w-3xl mx-auto px-6 text-center">
      <h1 className="font-display font-bold text-3xl">Article not found</h1>
      <Link to="/news" className="btn-primary mt-8 inline-flex">Back to news</Link>
    </div>
  );
  if (!article) return <div className="pt-40 pb-24 text-center text-[#4A5568]">Loading…</div>;

  return (
    <div data-testid="page-news-detail">
      <article className="pt-40 pb-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <Link to="/news" data-testid="news-back" className="inline-flex items-center gap-2 text-sm font-display text-[#003399] hover:text-[#00E5FF] transition-colors"><ArrowLeft size={14}/> {t("news.back")}</Link>
          <PillLabel tone="primary" testId="article-category">{article.category}</PillLabel>
          <h1 className="mt-4 font-display font-bold text-4xl md:text-5xl leading-tight tracking-tight text-[#0B1120]">{article.title}</h1>
          <div className="mt-6 text-sm text-slate-500 flex items-center gap-2"><Calendar size={14}/> {new Date(article.created_at).toLocaleDateString()}</div>
        </div>
        {article.cover_image && (
          <div className="max-w-5xl mx-auto px-6 mt-12">
            <img src={article.cover_image} alt={article.title} className="w-full aspect-[16/9] object-cover rounded-sm"/>
          </div>
        )}
        <div className="max-w-3xl mx-auto px-6 lg:px-8 mt-12 prose prose-lg">
          <p className="text-lg text-[#0B1120] leading-relaxed font-light whitespace-pre-line">{article.content}</p>
        </div>
      </article>
    </div>
  );
}
